import { useQueryClient } from "@tanstack/react-query"
import {
	CHAT_EVENT_NAMES,
	MARECZEK_EVENT_NAMES,
	type RealtimeMessage,
} from "@zplanem/responses"
import { useEffect } from "react"
import { queryKeys } from "../../lib/query-keys"
import { getPusherClient } from "../realtime/pusher-client"
import { applyRealtimeToChatCaches } from "./use-chat"
import { applyMareczekEventToThinkingCache } from "./use-mareczek-thinking"

export function usePropertyUserChatChannel(
	propertyId: string | undefined,
	userId: string | undefined,
): void {
	const queryClient = useQueryClient()
	useEffect(() => {
		if (!propertyId || !userId) return
		const channelName = `private-property-${propertyId}-user-${userId}-chat`
		const channel = getPusherClient().subscribe(channelName)
		const handlers: Record<string, (data: unknown) => void> = {}

		for (const eventName of CHAT_EVENT_NAMES) {
			const handler = (data: unknown) =>
				applyRealtimeToChatCaches(
					queryClient,
					propertyId,
					userId,
					data as RealtimeMessage,
				)
			handlers[eventName] = handler
			channel.bind(eventName, handler)
		}

		// OWNER-ONLY budget ping (ZPL-301): something in this property's budget
		// changed (chat-accepted expense, second device, background extraction
		// fill). The payload is an invalidation ping — just refetch; the key
		// prefix-matches the project-filtered budget queries too.
		const budgetHandler = () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.propertyBudget(propertyId),
			})
		handlers["budget-entry.changed"] = budgetHandler
		channel.bind("budget-entry.changed", budgetHandler)

		// Mareczek "thinking" indicator — a transient flag, not a chat-cache
		// write. Routed to the indicator cache by the payload's threadId.
		for (const eventName of MARECZEK_EVENT_NAMES) {
			const handler = (data: unknown) => {
				applyMareczekEventToThinkingCache(queryClient, {
					event: eventName,
					payload: data,
				} as RealtimeMessage)
			}
			handlers[eventName] = handler
			channel.bind(eventName, handler)
		}

		return () => {
			for (const [eventName, handler] of Object.entries(handlers)) {
				channel.unbind(eventName, handler)
			}
			getPusherClient().unsubscribe(channelName)
		}
	}, [propertyId, userId, queryClient])
}
